import * as THREE from 'three';

const MAX_ACTIVE = 8;
const LIFE_S = 0.85;

interface ActiveNum {
  el: HTMLDivElement;
  world: THREE.Vector3;
  life: number;
  headshot: boolean;
}

/** Screen-space floating damage popups at hit positions (pooled, max 8). */
export class FpsDamageNumbers {
  private readonly root: HTMLDivElement;
  private readonly pool: HTMLDivElement[] = [];
  private readonly active: ActiveNum[] = [];
  private readonly proj = new THREE.Vector3();
  private disposed = false;

  constructor(mount: HTMLElement) {
    this.root = document.createElement('div');
    this.root.style.cssText =
      'position:fixed;inset:0;pointer-events:none;z-index:37;overflow:hidden';
    mount.appendChild(this.root);

    for (let i = 0; i < MAX_ACTIVE; i++) {
      const el = document.createElement('div');
      el.style.cssText =
        'position:absolute;display:none;transform:translate(-50%,-50%);font:900 15px system-ui,sans-serif;' +
        'color:#fff;text-shadow:0 0 6px rgba(255,60,60,0.9),0 1px 3px #000;pointer-events:none;white-space:nowrap';
      this.pool.push(el);
      this.root.appendChild(el);
    }
  }

  spawn(worldPos: THREE.Vector3, damage: number, camera: THREE.Camera, headshot = false, isShield = false): void {
    if (this.disposed) return;

    let el: HTMLDivElement | undefined;
    if (this.active.length >= MAX_ACTIVE) {
      const oldest = this.active.shift();
      if (oldest) {
        el = oldest.el;
        el.style.display = 'none';
      }
    }
    if (!el) {
      el = this.pool.find((p) => p.style.display === 'none');
    }
    if (!el) return;

    el.textContent = `${Math.round(damage)}`;
    
    // Fortnite damage colors: Headshot = Yellow, Shield = Blue, White = Flesh
    if (headshot) {
      el.style.color = '#ffd700'; // Gold
      el.style.textShadow = '0 0 6px rgba(255,140,0,0.9), 0 1px 3px #000, inset 0 0 2px #fff';
      el.style.fontSize = '24px';
    } else if (isShield) {
      el.style.color = '#00e5ff'; // Cyan shield
      el.style.textShadow = '0 0 6px rgba(0,140,255,0.9), 0 1px 3px #000';
      el.style.fontSize = '20px';
    } else {
      el.style.color = '#ffffff'; // White flesh
      el.style.textShadow = '0 0 6px rgba(255,40,40,0.7), 0 1px 3px #000';
      el.style.fontSize = '18px';
    }
    
    el.style.display = 'block';
    el.style.opacity = '1';
    el.style.transform = 'translate(-50%,-50%) scale(1.5)'; // Pop in initial scale

    this.active.push({
      el,
      world: worldPos.clone(),
      life: LIFE_S,
      headshot,
    });
  }

  update(dt: number, camera: THREE.Camera, width: number, height: number): void {
    if (this.disposed) return;

    for (let i = this.active.length - 1; i >= 0; i--) {
      const a = this.active[i]!;
      a.life -= dt;
      if (a.life <= 0) {
        a.el.style.display = 'none';
        this.active.splice(i, 1);
        continue;
      }

      this.proj.copy(a.world);
      this.proj.project(camera);
      const x = (this.proj.x * 0.5 + 0.5) * width;
      const y = (-this.proj.y * 0.5 + 0.5) * height;
      const t = 1 - a.life / LIFE_S;
      const rise = t * 42;
      const fade = a.life < 0.28 ? a.life / 0.28 : 1;

      a.el.style.left = `${x}px`;
      a.el.style.top = `${y - rise}px`;
      a.el.style.opacity = String(fade);
      
      // Dynamic pop animation: expands quickly then shrinks slightly
      let scale = 1;
      if (t < 0.2) {
        scale = 1 + (t / 0.2) * 0.8; // Pop to 1.8
      } else {
        scale = 1.8 - ((t - 0.2) / 0.8) * 0.8; // Settle to 1.0
      }
      
      a.el.style.transform = `translate(-50%,-50%) scale(${scale})`;
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.active.length = 0;
    this.root.remove();
  }
}
